// TanStack Query wrappers for /v1/discoveries.
//
// Polling: `useDiscovery(id)` returns a query that refetches every 2s
// while non-terminal. Terminal states (succeeded/failed/canceled/timeout)
// stop polling entirely, so a left-open tab doesn't hammer the API
// forever.
//
// `useDiscoveriesList` opts into polling via `enableActivePolling`. When
// set, it refetches every 3s ONLY while at least one item in the response
// is non-terminal. As soon as every item lands in a terminal state, the
// poll stops on its own — same auto-shutoff invariant as `useDiscovery`.
// Default is off so list pages that don't care (most of them) don't pay
// the bandwidth.

import {
  type CreateDiscoveryBody,
  type CreateDiscoveryOptions,
  type Discovery,
  type DiscoveryListResponse,
  type ListDiscoveriesParams,
  cancelDiscovery,
  createDiscovery,
  deleteDiscovery,
  getDiscovery,
  isTerminal,
  listDiscoveries,
} from '@/lib/discoveries';
import {
  type UseMutationOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { v4 as uuidv4 } from 'uuid';

export const DISCOVERIES_QUERY_KEY = ['discoveries'] as const;
export const DISCOVERY_QUERY_KEY = (id: string) => ['discovery', id] as const;

export interface UseDiscoveriesListOptions extends ListDiscoveriesParams {
  /** Poll the list every 3s while any item is non-terminal. Default: off. */
  enableActivePolling?: boolean;
}

export function useDiscoveriesList(opts: UseDiscoveriesListOptions = {}) {
  const { enableActivePolling, ...params } = opts;
  return useQuery<DiscoveryListResponse, AxiosError>({
    queryKey: [...DISCOVERIES_QUERY_KEY, params],
    queryFn: () => listDiscoveries(params),
    staleTime: 5_000,
    refetchInterval: enableActivePolling
      ? (q) => {
          const items = q.state.data?.items ?? [];
          // Empty list → no work to track → no point polling.
          if (items.length === 0) return false;
          return items.some((i) => !isTerminal(i.status)) ? 3000 : false;
        }
      : false,
    refetchIntervalInBackground: false,
    // Returning to a backgrounded tab while a job ran should catch up
    // immediately instead of waiting for the next interval (or F5). The
    // hook still self-stops once every item is terminal.
    refetchOnWindowFocus: enableActivePolling ? true : false,
  });
}

export function useDiscovery(id: string | undefined) {
  return useQuery<Discovery, AxiosError>({
    queryKey: id ? DISCOVERY_QUERY_KEY(id) : ['discovery', 'noop'],
    queryFn: () => getDiscovery(id as string),
    enabled: !!id,
    staleTime: 0,
    refetchInterval: (q) => {
      const status = q.state.data?.status;
      if (isTerminal(status)) return false;
      // Tighter cadence (2s) while the worker is actually computing — the
      // progress bar wants visible motion. Looser cadence (10s) while the
      // job sits in the queue: progress_pct stays at 0 there and pinging
      // every 2s just hammers the API without telling the user anything new.
      return status === 'running' ? 2000 : 10000;
    },
    // Override the global `refetchOnWindowFocus: false` (query-client.ts) and
    // keep polling in a backgrounded tab so a running job's completion is
    // caught even if the user switched away mid-run — fixes "stale until F5".
    refetchOnWindowFocus: true,
    refetchIntervalInBackground: true,
  });
}

export interface CreateDiscoveryVariables {
  body: CreateDiscoveryBody;
  options?: CreateDiscoveryOptions;
}

export function useCreateDiscovery(
  options?: Omit<UseMutationOptions<Discovery, AxiosError, CreateDiscoveryVariables>, 'mutationFn'>,
) {
  const qc = useQueryClient();
  // Spread caller options FIRST so our wrapped onSuccess wins — otherwise the
  // call site would clobber the cache update + list invalidation entirely.
  return useMutation<Discovery, AxiosError, CreateDiscoveryVariables>({
    mutationFn: ({ body, options: o }) => createDiscovery(body, o),
    ...options,
    onSuccess: (job, ...rest) => {
      qc.invalidateQueries({ queryKey: DISCOVERIES_QUERY_KEY });
      qc.setQueryData(DISCOVERY_QUERY_KEY(job.id), job);
      options?.onSuccess?.(job, ...rest);
    },
  });
}

export function useCancelDiscovery() {
  const qc = useQueryClient();
  return useMutation<Discovery, AxiosError, string>({
    mutationFn: (id) => cancelDiscovery(id, { idempotencyKey: uuidv4() }),
    onSuccess: (job) => {
      qc.setQueryData(DISCOVERY_QUERY_KEY(job.id), job);
      qc.invalidateQueries({ queryKey: DISCOVERIES_QUERY_KEY });
    },
  });
}

export function useDeleteDiscovery() {
  const qc = useQueryClient();
  return useMutation<void, AxiosError, string>({
    mutationFn: (id) => deleteDiscovery(id),
    onSuccess: (_void, id) => {
      qc.removeQueries({ queryKey: DISCOVERY_QUERY_KEY(id) });
      qc.invalidateQueries({ queryKey: DISCOVERIES_QUERY_KEY });
    },
  });
}
